import { useQuery } from '@tanstack/react-query';
import Cookies from 'js-cookie';
import { mainApi } from '@/lib/api';
import { QUERY_KEYS } from '@/lib/constants';

interface IpInfo {
    country?: string;
    city?: string;
    region?: string;
    ip?: string;
}

const IP_INFO_COOKIE = 'ip_info';
const COOKIE_EXPIRY_DAYS = 30;

async function fetchIpInfo(): Promise<IpInfo> {
    // Check cookie first
    const cachedInfo = Cookies.get(IP_INFO_COOKIE);
    if (cachedInfo) {
        try {
            const parsed: IpInfo = JSON.parse(cachedInfo);
            if (parsed.country) {
                return parsed;
            }
        } catch {
            // Invalid cookie, continue to fetch
        }
    }

    // Fetch from API
    const response = await mainApi.get<IpInfo>('v1/services/get-ip-info');
    const ipInfo = response.data;

    // Cache in cookie
    Cookies.set(IP_INFO_COOKIE, JSON.stringify(ipInfo), { expires: COOKIE_EXPIRY_DAYS });

    return ipInfo;
}

/**
 * Hook to detect user's country via IP geolocation.
 * Uses React Query for caching and state management.
 * Note: Uses useQuery directly (not useStanQuery) since this is a public endpoint
 * that needs to work before authentication.
 */
export function useSessionCountry() {
    const { data, isLoading } = useQuery({
        queryKey: [QUERY_KEYS.SESSION_COUNTRY],
        queryFn: fetchIpInfo,
        staleTime: 1000 * 60 * 60 * 24, // 24 hours (cookie handles longer persistence)
        gcTime: 1000 * 60 * 60 * 24, // 24 hours
        retry: 1,
    });

    return {
        country: data?.country,
        isLoading,
    };
}
